import { useRef, useState } from 'react';
import { useTeam } from '../context/TeamContext';
import { AssigneeAvatar } from './AssigneeAvatar';

interface Props {
  value: string; // 'all' or a member userId
  onChange: (value: string) => void;
}

export function ViewerToggle({ value, onChange }: Props) {
  const { members } = useTeam();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  if (members.length <= 1) return null;

  const selected =
    value === 'all' ? null : members.find((m) => m.userId === value) ?? null;
  const label = value === 'all' ? 'Everyone' : selected?.displayName ?? 'Unknown';

  return (
    <div
      className="viewer-toggle"
      ref={wrapRef}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className={`viewer-toggle-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="viewer-toggle-label muted">Viewing:</span>
        {selected ? <AssigneeAvatar member={selected} size={18} /> : (
          <span className="viewer-everyone-dot" aria-hidden="true">∞</span>
        )}
        <span className="viewer-toggle-name">{label}</span>
        <span className="viewer-toggle-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="viewer-toggle-menu" role="listbox">
          <li>
            <button
              type="button"
              className={value === 'all' ? 'is-selected' : ''}
              onClick={() => {
                onChange('all');
                setOpen(false);
              }}
              role="option"
              aria-selected={value === 'all'}
            >
              <span className="viewer-everyone-dot" aria-hidden="true">∞</span>
              <span>Everyone</span>
            </button>
          </li>
          {members.map((m) => (
            <li key={m.userId}>
              <button
                type="button"
                className={value === m.userId ? 'is-selected' : ''}
                onClick={() => {
                  onChange(m.userId);
                  setOpen(false);
                }}
                role="option"
                aria-selected={value === m.userId}
              >
                <AssigneeAvatar member={m} size={18} />
                <span>{m.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
